import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Box,
  Alert,
  IconButton,
  Card,
  CardContent,
  Divider,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import DashboardLayout from "../../layouts/DashboardLayout";
import MDButton from "../../components/MDButton";
import { ThemeToggleSwitch } from "../../components/ThemeToggle";
import {
  getCurrentUser,
  updateUser,
  toggleUserNotifications,
} from "../../services/auth";

const SettingsPage = () => {
  const [errorMsg, setErrorMsg] = useState("");
  const passwordPattern = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notifMsg, setNotifMsg] = useState("");

  const [settings, setSettings] = useState({
    username: "",
    fullName: "",
    email: "",
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
    discord_username: "",
    discord_webhook: "",
  });

  const [showSaveAlert, setShowSaveAlert] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    async function fetchUser() {
      const user = await getCurrentUser();
      setSettings((prev) => ({
        ...prev,
        username: user.username,
        fullName: user.full_name,
        email: user.email,
        discord_username: user.discord_username || "",
        discord_webhook: user.discord_webhook || "",
      }));
      setNotificationsEnabled(user.notifications_enabled !== false);
    }
    fetchUser();
  }, []);

  const handleToggleNotifications = async () => {
    try {
      const newState = await toggleUserNotifications();
      setNotificationsEnabled(newState.notifications_enabled);
      setNotifMsg(
        newState.notifications_enabled
          ? "Notifications enabled."
          : "Notifications disabled.",
      );
      setTimeout(() => setNotifMsg(""), 2000);
    } catch {
      setNotifMsg("Failed to toggle notifications");
      setTimeout(() => setNotifMsg(""), 2000);
    }
  };

  const handleChange = (field) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    setErrorMsg("");
    if (settings.newPassword && !passwordPattern.test(settings.newPassword)) {
      setErrorMsg(
        "Password must be at least 8 characters, contain at least one uppercase letter and one number.",
      );
      return;
    }
    if (settings.newPassword !== settings.confirmPassword) {
      setErrorMsg("New password and confirmation do not match");
      return;
    }
    try {
      await updateUser({
        username: settings.username,
        full_name: settings.fullName,
        email: settings.email,
        old_password: settings.oldPassword,
        new_password: settings.newPassword,
        discord_username: settings.discord_username,
        discord_webhook: settings.discord_webhook,
      });
      setShowSaveAlert(true);
      setTimeout(() => setShowSaveAlert(false), 3000);
    } catch (error) {
      setErrorMsg(error.response?.data?.detail || "Update failed");
    }
  };

  return (
    <DashboardLayout>
      <Box>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          User Settings
        </Typography>

        {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
        {showSaveAlert && (
          <Alert severity="success">User data saved successfully!</Alert>
        )}

        <Grid container spacing={3} mt={1}>
          {/* Profile Info */}
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Profile Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <TextField
                  fullWidth
                  label="Username"
                  value={settings.username}
                  onChange={handleChange("username")}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Full Name"
                  value={settings.fullName}
                  onChange={handleChange("fullName")}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={settings.email}
                  onChange={handleChange("email")}
                  margin="normal"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Discord */}
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Discord Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" mb={1}>
                  <a
                    href="https://www.youtube.com/watch?v=mc3cV57m3mM"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#7289da",
                      textDecoration: "underline",
                    }}
                  >
                    How to get your Discord ID
                  </a>
                </Typography>

                <TextField
                  fullWidth
                  label="Discord ID"
                  value={settings.discord_username}
                  onChange={handleChange("discord_username")}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Discord Webhook URL"
                  type="url"
                  value={settings.discord_webhook}
                  onChange={handleChange("discord_webhook")}
                  margin="normal"
                />

                <Box mt={2} display="flex" alignItems="center" gap={2}>
                  <MDButton
                    variant={notificationsEnabled ? "contained" : "outlined"}
                    color={notificationsEnabled ? "success" : "warning"}
                    onClick={handleToggleNotifications}
                  >
                    {notificationsEnabled
                      ? "Disable Notifications"
                      : "Enable Notifications"}
                  </MDButton>
                  <Typography
                    variant="body2"
                    color={
                      notificationsEnabled ? "success.main" : "warning.main"
                    }
                  >
                    {notificationsEnabled
                      ? "Notifications are ON"
                      : "Notifications are OFF"}
                  </Typography>
                </Box>
                {notifMsg && (
                  <Alert
                    severity={notifMsg.includes("Failed") ? "error" : "info"}
                    sx={{ mt: 2 }}
                  >
                    {notifMsg}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Appearance */}
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Appearance
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <ThemeToggleSwitch />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Dark mode preference is saved per user on this device.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Password Change */}
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Change Password
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Password requirements:
                  </Typography>
                  <Box display="flex" flexDirection="column" ml={2} mt={1}>
                    <Typography variant="body2">
                      <CheckCircleOutline fontSize="small" color="success" /> At
                      least 8 characters
                    </Typography>
                    <Typography variant="body2">
                      <CheckCircleOutline fontSize="small" color="success" /> At
                      least one uppercase letter
                    </Typography>
                    <Typography variant="body2">
                      <CheckCircleOutline fontSize="small" color="success" /> At
                      least one number
                    </Typography>
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  label="Old Password"
                  type={showOldPassword ? "text" : "password"}
                  value={settings.oldPassword}
                  onChange={handleChange("oldPassword")}
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowOldPassword(!showOldPassword)}
                      >
                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? "text" : "password"}
                  value={settings.newPassword}
                  onChange={handleChange("newPassword")}
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={settings.confirmPassword}
                  onChange={handleChange("confirmPassword")}
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    ),
                  }}
                />

                <Box display="flex" justifyContent="flex-end" mt={3}>
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                  >
                    Save Changes
                  </MDButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
};

export default SettingsPage;
